import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDataChangedApi } from '../services/api';

export const DATA_CHANGED_QUERY_KEY = ['config', 'data-changed'] as const;
export const DATA_CHANGED_STORAGE_KEY = 'lastDataChangedAt';
export const DATA_CHANGED_POLL_MS = 60_000;

const readStored = (): string | null =>
  typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(DATA_CHANGED_STORAGE_KEY) : null;

const writeStored = (value: string) => {
  if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(DATA_CHANGED_STORAGE_KEY, value);
};

/**
 * Polls `GET /v2/config/data-changed` while `enabled` is true and hard-reloads
 * the page when the server-side `dataChangedAt` stamp differs from the one
 * recorded for this tab (sessionStorage). The first observed value is only
 * recorded, never acted on, so a fresh tab doesn't reload immediately.
 */
export const useDataChangedReload = (enabled: boolean) => {
  const { data } = useQuery({
    queryKey: DATA_CHANGED_QUERY_KEY,
    queryFn: async () => {
      const res = await getDataChangedApi();
      if (res.error != null) throw new Error(res.error);
      return res.dataChangedAt ?? '';
    },
    enabled,
    refetchInterval: DATA_CHANGED_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: DATA_CHANGED_POLL_MS,
    retry: false,
  });

  useEffect(() => {
    if (!enabled || data === undefined) return;
    const stored = readStored() ?? '';
    if (stored === '') {
      writeStored(data);
      return;
    }
    if (data !== stored) {
      writeStored(data);
      window.location.reload();
    }
  }, [data, enabled]);
};
